// ============================================
// Import sản phẩm từ file CSV/XLSX (sheet 2 — mục #13/#21)
// ============================================
//
// POST /products/import/ là endpoint **multipart/form-data**, mà `ApiClient`
// chỉ biết gửi JSON. Nên hook này dùng `fetch` thẳng, đúng cách đã làm với
// `useUploadImage` — không phải vì tiện, mà vì đặt `Content-Type` thủ công cho
// FormData sẽ làm mất `boundary` và server không đọc được file.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { ProductImportResponse } from '@conai/shared-types';
import { tokenStorage } from '../config/api';
import { env } from '../config/env';
import { queryKeys } from '../config/query-client';
import { useI18n } from '../i18n';

/** Trần của BE — vượt là 400 ở mức file, không phải lỗi từng dòng. */
export const PRODUCT_IMPORT_MAX_ROWS = 2000;
export const PRODUCT_IMPORT_MAX_BYTES = 5 * 1024 * 1024;

export interface ImportProductsInput {
  file: File;
  /** `true` = chỉ kiểm tra, KHÔNG ghi gì. Luôn chạy thử trước khi ghi thật. */
  dryRun?: boolean;
}

async function postImport({ file, dryRun }: ImportProductsInput): Promise<ProductImportResponse> {
  const token = await tokenStorage.getAccessToken();
  if (!token) throw new Error('Not authenticated');

  const formData = new FormData();
  formData.append('file', file);
  if (dryRun) formData.append('dry_run', 'true');

  const response = await fetch(`${env.apiBaseUrl}/products/import/`, {
    method: 'POST',
    // KHÔNG set Content-Type: trình duyệt tự thêm kèm boundary của FormData.
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const payload = await response.json().catch(() => ({}));

  // 400 = lỗi mức FILE (thiếu cột sku, cột lạ, quá 2000 dòng, quá 5MB). Lỗi
  // từng dòng KHÔNG rơi vào đây — chúng đi kèm HTTP 200 trong `failed[]`.
  if (!response.ok) {
    const msg =
      (payload as { message?: string; detail?: string }).message ??
      (payload as { detail?: string }).detail ??
      `Import failed (${response.status})`;
    throw new Error(msg);
  }

  // Envelope {data, code, message} — bóc `data` nếu có.
  const body = payload as { data?: ProductImportResponse } & ProductImportResponse;
  const raw = body.data ?? body;

  // BE trả các con số PHẲNG trong `data`: {total, created, updated, failed,
  // dry_run} — KHÔNG bọc trong `summary` như các endpoint bulk khác (đo trên QA
  // 09/09/2026: chạy thử 3 dòng trả `created: 3` nhưng màn hình hiện 0).
  // Chuẩn hoá ngay tại đây để cả hook lẫn modal chỉ phải đọc một chỗ; vẫn ưu
  // tiên `summary` nếu về sau BE bọc lại, nên sửa hai chiều đều không vỡ.
  const flat = raw as ProductImportResponse & {
    total?: number;
    created?: number;
    updated?: number;
  };

  return {
    ...raw,
    summary: raw.summary ?? {
      total: flat.total ?? 0,
      created: flat.created ?? 0,
      updated: flat.updated ?? 0,
    },
  };
}

export function useImportProducts() {
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: postImport,
    onSuccess: (res, variables) => {
      // Chạy thử thì KHÔNG invalidate: không có gì thay đổi, refetch chỉ tạo
      // cảm giác sai rằng dữ liệu đã được ghi.
      if (!variables.dryRun) {
        queryClient.invalidateQueries({ queryKey: queryKeys.products.lists() });
        queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      }

      const created = res.summary?.created ?? 0;
      const updated = res.summary?.updated ?? 0;
      const failed = res.failed?.length ?? 0;

      if (variables.dryRun) {
        toast.success(
          t('importDryRunDone')
            .replace('{created}', String(created))
            .replace('{updated}', String(updated))
            .replace('{failed}', String(failed))
        );
        return;
      }

      if (failed === 0) {
        toast.success(
          t('importDone').replace('{created}', String(created)).replace('{updated}', String(updated))
        );
      } else {
        toast.error(t('importPartial').replace('{failed}', String(failed)));
      }
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t('importFailed'));
    },
  });
}
